import type { WebContainer } from '@webcontainer/api';
import { workbenchStore } from '../stores/workbench';
import { registerAssetInStore } from '../runtime/tool-adapters/writeAsset';
import { executePrune } from '../runtime/tool-adapters/pruneWorkspace';
import { getHeadlessTemplateFiles } from '../templates/headlessTemplate';
import { ensureHotEditBridgeFiles } from './ensureHotEditBridge';
import { HOT_EDIT_BRIDGE_REL_PATHS } from './mergedFiles';
import {
  readSnapshotZipEntries,
  throwIfAborted,
  type SnapshotZipEntry,
} from './snapshotZipEntries';
import {
  chooseSnapshotSwitchMode,
  findSnapshotMismatches,
  planSnapshotSwitch,
  type SnapshotSwitchReason,
} from './snapshotSwitchPlan';
import { wcPerfAsync } from './workbenchPerf';
import {
  ensureDependenciesMatchLockfile,
  restartDevServerAfterSnapshot,
  stopDevServerForRestore,
} from '../webcontainer/index';

/** Parallel writes reduce wall time for large restores. */
const SNAPSHOT_WRITE_CONCURRENCY = 14;

async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  if (items.length === 0) return;
  let i = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const idx = i++;
      if (idx >= items.length) break;
      await fn(items[idx]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
}

async function writeEntry(wc: WebContainer, entry: SnapshotZipEntry): Promise<void> {
  const dir = entry.path.includes('/') ? entry.path.substring(0, entry.path.lastIndexOf('/')) : '';
  if (dir) {
    await wc.fs.mkdir(dir, { recursive: true });
  }
  if (entry.isBinary) {
    await wc.fs.writeFile(entry.path, entry.u8!);
    registerAssetInStore(entry.path, entry.base64!);
    return;
  }
  await wc.fs.writeFile(entry.path, entry.text!);
  workbenchStore.files.setKey(entry.path, { content: entry.text!, modified: false });
  workbenchStore.recordToolWriteSnapshot(entry.path, entry.text!);
}

export interface SnapshotApplyResult {
  /**
   * `fast`: only the differing files were written and the dev server kept running.
   * `restart`: every file was written with the dev server stopped; the caller starts it again
   * with `restartDevServerAfterSnapshot`.
   */
  mode: 'fast' | 'restart';
  reason: SnapshotSwitchReason;
  written: number;
  removed: number;
}

/**
 * Apply a snapshot zip (same shape as buildPrototypeSnapshotZipBlob) onto WebContainer + workbench
 * store. Replaces prior user files/assets — does not merge.
 *
 * P6.4d-2: when this tab's store is known to mirror the container (`filesTrustedFor`), a small
 * source-only switch writes just the differing files while Vite keeps running. The preview
 * detaches for the writes (`previewSwitching`) and a fresh page loads the new version. The result
 * is checked against the version; anything uncertain — first restore, dependency or config
 * changes, many files, a mismatch — takes the full path below.
 */
export async function applySnapshotZipToWebContainer(
  wc: WebContainer,
  blob: Blob,
  signal?: AbortSignal,
): Promise<SnapshotApplyResult> {
  return wcPerfAsync('wc:snapshot:applyZip', async () => {
    workbenchStore.restoreInProgress.set(true);
    let devServerStopped = false;
    let previewDetached = false;
    try {
      throwIfAborted(signal);

      const entries = await readSnapshotZipEntries(blob, signal);
      throwIfAborted(signal);

      // Whatever the preview last reported about the workspace is about to stop being true.
      workbenchStore.previewShowsTemplatePlaceholder.set(false);

      const templateFiles = getHeadlessTemplateFiles();
      const protectedPaths = new Set(
        HOT_EDIT_BRIDGE_REL_PATHS.map((p) => p.replace(/\\/g, '/').replace(/^\/+/, '')),
      );
      const workspace = () => ({
        entries,
        currentFiles: workbenchStore.files.get(),
        currentAssets: workbenchStore.assets.get(),
        templateFiles,
        protectedPaths,
      });
      const plan = planSnapshotSwitch(workspace());
      const decision = chooseSnapshotSwitchMode(plan, {
        trusted: workbenchStore.filesTrustedFor.get() === wc,
        devServerReady: workbenchStore.devServerStatus.get() === 'ready',
      });
      // Only a restore that finishes re-establishes that the store mirrors this container, so an
      // aborted or failed one leaves the next restore on the full path.
      workbenchStore.filesTrustedFor.set(null);
      const byPath = new Map(entries.map((entry) => [entry.path, entry]));
      const keepPaths = entries.map((entry) => entry.path);

      if (decision.mode === 'fast') {
        // No page is attached while files change: the preview counts as not ready, its iframe
        // unmounts, and a fresh one loads the finished version (P2.5a). Vite keeps running.
        workbenchStore.previewSwitching.set(true);
        previewDetached = true;
        await runPool(
          plan.changedPaths.map((path) => byPath.get(path)!),
          SNAPSHOT_WRITE_CONCURRENCY,
          async (entry) => {
            if (signal?.aborted) return;
            await writeEntry(wc, entry);
          },
        );
        throwIfAborted(signal);
        for (const path of plan.removedPaths) {
          await wc.fs.rm(path, { force: true }).catch(() => undefined);
          workbenchStore.removeFile(path);
        }
        await executePrune(wc, keepPaths);
        throwIfAborted(signal);
        await ensureHotEditBridgeFiles(wc);

        const mismatches = findSnapshotMismatches(workspace());
        if (mismatches.length === 0) {
          workbenchStore.hasUnbuiltEdits.set(false);
          workbenchStore.filesTrustedFor.set(wc);
          return {
            mode: 'fast' as const,
            reason: 'fast' as const,
            written: plan.changedPaths.length,
            removed: plan.removedPaths.length,
          };
        }
        console.warn(
          '[Workbench] fast snapshot switch did not match the version; restoring every file',
          mismatches.slice(0, 10),
        );
      }

      // Vite stays down for the writes, prune and bridge patch below; callers start it once
      // with `restartDevServerAfterSnapshot` when this resolves.
      await stopDevServerForRestore();
      devServerStopped = true;

      workbenchStore.files.set({});
      workbenchStore.assets.set({});
      workbenchStore.lastToolWrittenByPath.set({});
      workbenchStore.fileConflicts.set({});
      // Restoring wholesale replaces any pending manual edits with the (already-built)
      // snapshot content, so there's nothing left "unbuilt" relative to what's now loaded.
      workbenchStore.hasUnbuiltEdits.set(false);

      await runPool(entries, SNAPSHOT_WRITE_CONCURRENCY, async (entry) => {
        if (signal?.aborted) return;
        await writeEntry(wc, entry);
      });

      throwIfAborted(signal);

      // The writes above only ever add/overwrite paths present in this snapshot — a file
      // that existed in the WebContainer from a later/different generation (e.g. restoring
      // an earlier turn after a later one added a page) is never removed, so it stays on
      // disk and can affect the next build's type-check even though the app's own file
      // list no longer references it. `executePrune` removes stale generated files under
      // `src/` while preserving template infra, the hot-edit bridge, and this snapshot's
      // own paths.
      await executePrune(wc, keepPaths);

      throwIfAborted(signal);

      await ensureHotEditBridgeFiles(wc);
      // A version with other dependencies: mount or install them before Vite starts again.
      await ensureDependenciesMatchLockfile(wc);

      workbenchStore.filesTrustedFor.set(wc);
      return {
        mode: 'restart' as const,
        reason: decision.mode === 'fast' ? ('mismatch' as const) : decision.reason,
        written: entries.length,
        removed: 0,
      };
    } catch (err) {
      // Callers skip their restart when the restore throws; without this the preview would
      // stay on a stopped server until the silent-preview recovery kicks in.
      if (devServerStopped) void restartDevServerAfterSnapshot(wc);
      throw err;
    } finally {
      if (previewDetached) workbenchStore.previewSwitching.set(false);
      window.setTimeout(() => {
        workbenchStore.restoreInProgress.set(false);
      }, 4_500);
    }
  });
}
